/**
 * `@eis/insuranceservice-sdk` — two clients, one service.
 *
 * The configuration API and the instance API are separate proto packages and separate OpenAPI
 * documents (decision S-9): different personas, different auth scopes, and — the reason the split
 * is not cosmetic — different rules for the word "version". Configuration versions are immutable
 * and pinned; instance versions are bitemporal and independent (architecture §A.2). The types are
 * therefore exported under two namespaces rather than merged into one flat surface, so no call
 * site can name a `version` without naming its layer: `ConfigApi.TarifVersion` versus
 * `InstanceApi.SnapshotEnvelope`.
 *
 * `./generated` comes from api/openapi/insurance.{config,instance}.v1.openapi.yaml via
 * `pnpm gen:sdk`; never hand-edited, diffed by `make gen-check` (rule 26).
 */
import {
  createEisClient,
  createEtagStore,
  type EisClient,
  type EisClientOptions,
  type EtagStore,
} from '@eis/sdk-runtime';
import type { paths as ConfigPaths } from './generated/insurance.config.v1';
import type { paths as InstancePaths } from './generated/insurance.instance.v1';

export type * as ConfigApi from './generated/insurance.config.v1';
export type * as InstanceApi from './generated/insurance.instance.v1';

export {
  ConflictError,
  isConflictError,
  createEtagStore,
  type ConflictKind,
  type EtagStore,
  type TokenProvider,
  type TraceparentProvider,
  type EisClientOptions,
  // The session middleware's client half (SDD-019 S-6): what is retried, and when.
  createSessionRefreshMiddleware,
  isSafeToReplay,
  type SessionRefreshOptions,
} from '@eis/sdk-runtime';

// The list standard's client half (SDD-017 §10, L-13): builders, the pager, the catalog types.
export {
  escapeFilterValue,
  filters,
  isListError,
  listErrorOf,
  ListPageError,
  OPERATORS,
  paginate,
  search,
  type FieldType,
  type FilterCatalog,
  type FilterField,
  type FilterValue,
  type ListErrorCode,
  type ListErrorDetail,
  type ListPage,
  type Operator,
} from '@eis/sdk-runtime';

export type ConfigClient = EisClient<ConfigPaths>;
export type InstanceClient = EisClient<InstancePaths>;

export function createConfigClient(options: EisClientOptions): ConfigClient {
  return createEisClient<ConfigPaths>(options);
}

export function createInstanceClient(options: EisClientOptions): InstanceClient {
  return createEisClient<InstancePaths>(options);
}

export interface InsuranceServiceClients {
  readonly config: ConfigClient;
  readonly instance: InstanceClient;
  /** Shared: one gateway, one set of resources, therefore one set of beliefs about their ETags. */
  readonly etags: EtagStore;
}

/**
 * Both clients against one gateway, sharing an ETag store. Prefer this over building the two
 * separately: a config read and an instance write are the same session, and two stores would
 * mean the same resource could be guarded by one client and unguarded by the other.
 */
export function createInsuranceServiceClients(options: EisClientOptions): InsuranceServiceClients {
  const etags = options.etagStore ?? createEtagStore();
  const shared: EisClientOptions = { ...options, etagStore: etags };
  return {
    config: createConfigClient(shared),
    instance: createInstanceClient(shared),
    etags,
  };
}
